import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { COLORS } from '../../styles/Colors';

// ========== CONFIGURAZIONE CATEGORIE ==========
const CATEGORY_STYLES = {
  'Free Palestine': { color: '#ff0000ff', icon: 'fas fa-calendar-alt', categoryIds: [35], name: 'Free Palestine' },
  'spettacolo': { color: '#e74c3c', icon: 'fas fa-theater-masks', categoryIds: [16, 2, 18, 17, 3], name: 'Spettacolo' },
  'musica': { color: '#e74c3c', icon: 'fas fa-music', categoryIds: [16, 2, 18, 17, 1, 3, 27, 26, 25, 24, 23, 22, 28, 29, 32], name: 'Musica' },
  'culinaria': { color: '#f39c12', icon: 'fas fa-utensils', categoryIds: [4, 9], name: 'Enogastronomia' },
  'sport': { color: '#27ae60', icon: 'fas fa-running', categoryIds: [20, 7, 21], name: 'Sport & Movimento' },
  'cultura': { color: '#9b59b6', icon: 'fas fa-university', categoryIds: [10, 5, 15, 19, 34], name: 'Arte & Cultura' },
  'eventi': { color: '#e91e63', icon: 'fas fa-calendar-star', categoryIds: [8], name: 'Festival & Eventi' },
  'natura': { color: '#2ecc71', icon: 'fas fa-leaf', categoryIds: [11], name: 'Natura & Outdoor' },
  'lifestyle': { color: '#f1c40f', icon: 'fas fa-star', categoryIds: [14], name: 'Lifestyle & Formazione' },
  'digitale': { color: '#34495e', icon: 'fas fa-laptop', categoryIds: [33], name: 'Tech' },
  'default': { color: '#95a5a6', icon: 'fas fa-calendar-alt', categoryIds: [], name: 'Altri Eventi' }
};

const getMacroCategoryForCategory = (categoryId) => {
  for (const [, config] of Object.entries(CATEGORY_STYLES)) {
    if (config.categoryIds.includes(categoryId)) {
      return config;
    }
  }
  return CATEGORY_STYLES.default;
};

// Hook per rilevare la modalità mobile
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  return isMobile;
};

// ========== STYLED COMPONENTS ==========
const FilterPanelContainer = styled.div`
  position: absolute;
  top: 70px;
  left: ${props => props.isOpen ? '10px' : '-300px'};
  width: 280px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  transition: left 0.3s ease;
  z-index: 1000;
  max-height: calc(100vh - 120px);
  overflow-y: auto;
  
  @media (max-width: 768px) {
    width: 85%;
    max-width: 300px;
    top: 60px;
    bottom: auto;
    max-height: calc(100vh - 80px);
  }
`;

const FilterHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 15px;
  border-bottom: 1px solid #eee;
  position: sticky;
  top: 0;
  background: white;
  z-index: 2;

  h2 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  cursor: pointer;
  color: #666;
  &:hover { color: #333; }
`;

const FilterSection = styled.div`
  padding: 12px 15px;
  border-bottom: 1px solid #eee;
  &:last-of-type {
    border-bottom: none;
  }
  h3 {
    margin: 0 0 10px 0;
    font-size: 16px;
    font-weight: 500;
  }
`;

const SearchForm = styled.form`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 40px 10px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: ${COLORS.primary || '#f39c12'};
    box-shadow: 0 0 0 2px rgba(243, 156, 18, 0.2);
  }
  &::placeholder { color: #999; font-style: italic; }
`;

const SearchIconButton = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none; border: none; color: #666;
  font-size: 14px; cursor: pointer; padding: 4px;
  border-radius: 50%; transition: all 0.2s ease;
  
  &:hover {
    color: ${COLORS.primary || '#f39c12'};
    background-color: rgba(243, 156, 18, 0.1);
  }
`;

const SearchClearButton = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none; border: none; color: #999;
  font-size: 16px; cursor: pointer; padding: 2px;
  border-radius: 50%; transition: all 0.2s ease;
  
  &:hover { color: #666; background-color: rgba(0,0,0,0.05); }
`;

const MacroCategorySection = styled.div`
  margin-bottom: 15px;
  &:last-child { margin-bottom: 0; }
`;

const MacroCategoryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  padding: 6px 8px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 0.2s ease;
  &:hover { background-color: rgba(0,0,0,0.05); }
`;

const MacroCategoryInfo = styled.div`
  display: flex;
  align-items: center;
`;

const MacroCategoryIcon = styled.i`
  color: ${props => props.color};
  margin-right: 8px;
  font-size: 14px;
  width: 16px;
  text-align: center;
`;

const MacroCategoryTitle = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #555;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const MacroCategoryCheckbox = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid ${props => props.color};
  border-radius: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.isSelected ? props.color : 'transparent'};
  transition: all 0.2s ease;
  
  &::after {
    content: '✓';
    color: white;
    font-size: 10px;
    font-weight: bold;
    opacity: ${props => props.isSelected ? 1 : 0};
    transition: opacity 0.2s ease;
  }
`;

const MacroCategoryPartialCheckbox = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid ${props => props.color};
  border-radius: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.color}40;
  
  &::after {
    content: '−';
    color: ${props => props.color};
    font-size: 12px;
    font-weight: bold;
  }
`;

const SubCategoryList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-left: 20px;
`;

const SubCategoryItem = styled.div`
  display: flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${props => props.isSelected ? props.color : '#f8f9fa'};
  color: ${props => props.isSelected ? 'white' : '#555'};
  border: 1px solid ${props => props.isSelected ? props.color : '#e9ecef'};
  
  &:hover {
    background-color: ${props => props.isSelected ? props.color : props.color + '20'};
    border-color: ${props => props.color};
  }
`;

const DropdownContainer = styled.div`
  position: relative;
`;

const DropdownButton = styled.button`
  width: 100%;
  padding: 10px 12px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  &:hover { border-color: ${COLORS.primary || '#f39c12'}; }
  &:focus {
    outline: none;
    border-color: ${COLORS.primary || '#f39c12'};
    box-shadow: 0 0 0 2px rgba(243, 156, 18, 0.2);
  }
`;

const DropdownArrow = styled.span`
  transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  transition: transform 0.2s ease;
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ddd;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  z-index: 1001;
  max-height: 300px;
  overflow-y: auto;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transform: ${props => props.isOpen ? 'scaleY(1)' : 'scaleY(0)'};
  transform-origin: top;
  transition: all 0.2s ease;
  margin-top: 4px;
`;

const DropdownHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  font-weight: 600;
  position: sticky;
  top: 0;
  z-index: 1;
`;

const DropdownCloseIcon = styled.button`
  background: none; border: none; font-size: 18px;
  color: #6c757d; cursor: pointer;
`;

const DropdownMacroSection = styled.div`
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
  &:last-child { border-bottom: none; }
`;

const DropdownMacroHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 12px;
  background: #f8f9fa;
  font-size: 12px;
  font-weight: 600;
  color: #555;
  text-transform: uppercase;
  cursor: pointer;
  &:hover { background: #e9ecef; }
`;

const DropdownItem = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 20px;
  cursor: pointer;
  &:hover { background-color: ${props => props.color + '10'}; }
`;

const Checkbox = styled.input`
  margin-right: 10px;
  cursor: pointer;
  accent-color: ${props => props.accentColor || COLORS.primary || '#f39c12'};
`;

const CategoryLabel = styled.span`
  font-size: 13px;
`;

const CategoryColor = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${props => props.color || '#f39c12'};
  margin-left: 8px;
`;

const SelectedCount = styled.span`
  background: ${COLORS.primary || '#f39c12'};
  color: white;
  font-size: 12px;
  padding: 2px 6px;
  border-radius: 10px;
  margin-left: 8px;
`;

const DateContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;
`;

const DateLabel = styled.label`
  width: 40px;
  font-size: 14px;
`;

const DateInput = styled.input`
  flex: 1;
  padding: 6px 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
`;

const DateQuickButtons = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 10px;
`;

const QuickDateButton = styled.button`
  flex: 1;
  padding: 8px 12px;
  background: transparent;
  color: ${COLORS.primary || '#f39c12'};
  border: 1px solid ${COLORS.primary || '#f39c12'};
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover {
    background: ${COLORS.primary || '#f39c12'};
    color: white;
  }
`;

const RadiusContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const RadiusValue = styled.div`
  text-align: center;
  font-size: 16px;
  font-weight: 500;
`;

const RadiusSlider = styled.input`
  width: 100%;
  -webkit-appearance: none;
  height: 4px;
  background: #ddd;
  border-radius: 2px;
  
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: ${COLORS.primary || '#f39c12'};
    cursor: pointer;
  }
  
  &::-moz-range-thumb {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: ${COLORS.primary || '#f39c12'};
    cursor: pointer;
    border: none;
  }
`;

const RadiusLabels = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #666;
`;

const ButtonsContainer = styled.div`
  padding: 15px 15px 10px;
  border-top: 1px solid #eee;
  position: sticky;
  bottom: 0;
  background: white;
  z-index: 2;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ApplyButton = styled.button`
  width: 100%;
  padding: 10px;
  background-color: ${COLORS.primary || '#f39c12'};
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: ${COLORS.primaryDark || '#e67e22'};
  }
`;

const ResetButton = styled.button`
  width: 100%;
  padding: 8px;
  background-color: transparent;
  color: #666;
  border: none;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    text-decoration: underline;
  }
`;

const CategorySectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
`;

const CollapseIcon = styled.i`
  transition: transform 0.2s ease;
  transform: ${props => props.isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)'};
`;

const CategoryContent = styled.div`
  /* Abbiamo ridotto l'altezza per non occupare troppo spazio nel pannello */
  max-height: ${props => props.isCollapsed ? '0' : '350px'}; 
  
  overflow-y: auto; 
  
  transition: max-height 0.3s ease-out;
`;

const DropdownCloseButton = styled.button`
  width: 100%;
  padding: 12px;
  background-color: #f8f9fa;
  border: none;
  border-top: 1px solid #dee2e6;
  color: #6c757d;
  font-weight: 500;
  cursor: pointer;
  position: sticky;
  bottom: 0;
`;

// ========== COMPONENTE PRINCIPALE ==========
const FilterPanel = ({ isOpen, onClose, onApplyFilters, userLocation, categories = [] }) => {
  const isMobile = useIsMobile();
  const dropdownRef = useRef(null);

  // Stati dei filtri
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [searchRadius, setSearchRadius] = useState(50);
  const [searchText, setSearchText] = useState('');
  
  // Stati UI e di errore
  const [dateError, setDateError] = useState('');
  const [searchError, setSearchError] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isCategoriesCollapsed, setIsCategoriesCollapsed] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // --- GESTORI DI EVENTI ---
  const handleStartDateChange = (e) => {
    const newStartDate = e.target.value;
    setDateError('');
    if (endDate && newStartDate > endDate) {
      setEndDate('');
      setDateError('Data "A" resettata perché precedente alla nuova data "Da"');
      setTimeout(() => setDateError(''), 4000);
    }
    setStartDate(newStartDate);
  };

  const handleEndDateChange = (e) => {
    const newEndDate = e.target.value;
    setDateError('');
    if (startDate && newEndDate < startDate) {
      setStartDate('');
      setDateError('Data "Da" resettata perché successiva alla nuova data "A"');
      setTimeout(() => setDateError(''), 4000);
    }
    setEndDate(newEndDate);
  };

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
    setSearchError('');
  };

  const handleSearchClear = () => {
    setSearchText('');
    setSearchError('');
    onApplyFilters({ searchText: '' }); // Applica subito il filtro vuoto
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    handleApplyFilters();
  };

  const handleApplyFilters = () => {
    if (searchText.length > 0 && searchText.length < 2) {
      setSearchError('Inserisci almeno 2 caratteri');
      return;
    }
    setSearchError('');
    
    const filters = {
      categories: selectedCategories,
      startDate,
      endDate,
      searchRadius: userLocation ? searchRadius : null,
      searchText: searchText.trim(),
    };
    onApplyFilters(filters);
    onClose();
  };

  const handleResetFilters = () => {
    setSelectedCategories([]);
    setStartDate('');
    setEndDate('');
    setSearchRadius(50);
    setSearchText('');
    setSearchError('');
    setDateError('');
    setIsDropdownOpen(false);
    
    onApplyFilters({
      categories: [],
      startDate: '',
      endDate: '',
      searchRadius: null,
      searchText: '',
      resetUserLocation: true
    });
  };

  const handleTodayClick = () => {
    const today = format(new Date(), 'yyyy-MM-dd');
    setStartDate(today);
    setEndDate(today);
  };

  const handleTomorrowClick = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = format(tomorrow, 'yyyy-MM-dd');
    setStartDate(tomorrowStr);
    setEndDate(tomorrowStr);
  };

  const handleWeekendClick = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const daysToSaturday = (dayOfWeek === 6) ? 0 : 6 - dayOfWeek;
    const nextSaturday = new Date();
    nextSaturday.setDate(today.getDate() + daysToSaturday);
    const nextSunday = new Date(nextSaturday);
    nextSunday.setDate(nextSaturday.getDate() + 1);
    setStartDate(format(nextSaturday, 'yyyy-MM-dd'));
    setEndDate(format(nextSunday, 'yyyy-MM-dd'));
  };

  // --- LOGICA CATEGORIE ---
  const groupCategoriesByMacro = () => {
    const grouped = {};
    Object.keys(CATEGORY_STYLES).forEach(macroKey => {
      grouped[macroKey] = { ...CATEGORY_STYLES[macroKey], categories: [] };
    });
    categories.forEach(category => {
      const macroConfig = getMacroCategoryForCategory(category.id);
      const macroKey = Object.keys(CATEGORY_STYLES).find(key => CATEGORY_STYLES[key] === macroConfig) || 'default';
      if (grouped[macroKey]) {
        grouped[macroKey].categories.push(category);
      }
    });
    Object.keys(grouped).forEach(key => {
      if (grouped[key].categories.length === 0) {
        delete grouped[key];
      }
    });
    return grouped;
  };

  const toggleCategory = (categoryId) => {
    setSelectedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const toggleMacroCategory = (macroData) => {
    const macroCategoryIds = macroData.categories.map(cat => cat.id);
    const areAllSelected = macroCategoryIds.every(id => selectedCategories.includes(id));
    
    setSelectedCategories(prev => {
      if (areAllSelected) {
        return prev.filter(id => !macroCategoryIds.includes(id));
      } else {
        const newSelected = new Set([...prev, ...macroCategoryIds]);
        return Array.from(newSelected);
      }
    });
  };
  
  // --- FUNZIONI DI RENDER ---
  const getDropdownButtonText = () => {
    if (selectedCategories.length === 0) return 'Seleziona categorie';
    if (selectedCategories.length === 1) {
      const category = categories.find(cat => cat.id === selectedCategories[0]);
      return category?.name || '1 categoria';
    }
    return `${selectedCategories.length} categorie`;
  };
  
  const renderDesktopCategorySelector = () => {
    const groupedCategories = groupCategoriesByMacro();
    return (
      <div>
        {Object.entries(groupedCategories).map(([macroKey, macroData]) => {
          const macroCategoryIds = macroData.categories.map(cat => cat.id);
          const areAllSelected = macroCategoryIds.length > 0 && macroCategoryIds.every(id => selectedCategories.includes(id));
          const areSomeSelected = !areAllSelected && macroCategoryIds.some(id => selectedCategories.includes(id));
          
          return (
            <MacroCategorySection key={macroKey}>
              <MacroCategoryHeader onClick={() => toggleMacroCategory(macroData)}>
                <MacroCategoryInfo>
                  <MacroCategoryIcon className={macroData.icon} color={macroData.color} />
                  <MacroCategoryTitle>{macroData.name}</MacroCategoryTitle>
                </MacroCategoryInfo>
                {areAllSelected ? (
                  <MacroCategoryCheckbox isSelected={true} color={macroData.color} />
                ) : areSomeSelected ? (
                  <MacroCategoryPartialCheckbox color={macroData.color} />
                ) : (
                  <MacroCategoryCheckbox isSelected={false} color={macroData.color} />
                )}
              </MacroCategoryHeader>
              <SubCategoryList>
                {macroData.categories.map(category => (
                  <SubCategoryItem
                    key={category.id}
                    isSelected={selectedCategories.includes(category.id)}
                    color={macroData.color}
                    onClick={(e) => { e.stopPropagation(); toggleCategory(category.id); }}
                  >
                    {category.name}
                  </SubCategoryItem>
                ))}
              </SubCategoryList>
            </MacroCategorySection>
          );
        })}
      </div>
    );
  };

  const renderMobileCategorySelector = () => {
      const groupedCategories = groupCategoriesByMacro();
      return (
        <DropdownContainer ref={dropdownRef}>
          <DropdownButton onClick={() => setIsDropdownOpen(!isDropdownOpen)} type="button">
            <span>{getDropdownButtonText()}</span>
            <div>
              {selectedCategories.length > 0 && <SelectedCount>{selectedCategories.length}</SelectedCount>}
              <DropdownArrow isOpen={isDropdownOpen}>▼</DropdownArrow>
            </div>
          </DropdownButton>
          <DropdownMenu isOpen={isDropdownOpen}>
            <DropdownHeader>
              <span>Seleziona Categorie</span>
              <DropdownCloseIcon onClick={() => setIsDropdownOpen(false)}>×</DropdownCloseIcon>
            </DropdownHeader>
            {Object.entries(groupedCategories).map(([macroKey, macroData]) => (
              <DropdownMacroSection key={macroKey}>
                  <DropdownMacroHeader onClick={() => toggleMacroCategory(macroData)}>
                    {macroData.name}
                  </DropdownMacroHeader>
                  {macroData.categories.map(category => (
                    <DropdownItem key={category.id} color={macroData.color} onClick={() => toggleCategory(category.id)}>
                      <Checkbox
                        type="checkbox"
                        checked={selectedCategories.includes(category.id)}
                        onChange={() => {}}
                        accentColor={macroData.color}
                      />
                      <CategoryLabel>{category.name}</CategoryLabel>
                      <CategoryColor color={macroData.color} />
                    </DropdownItem>
                  ))}
              </DropdownMacroSection>
            ))}
            <DropdownCloseButton onClick={() => setIsDropdownOpen(false)}>Chiudi</DropdownCloseButton>
          </DropdownMenu>
        </DropdownContainer>
      );
  };
  
  // --- RENDER PRINCIPALE ---
  return (
    <FilterPanelContainer isOpen={isOpen}>
      <FilterHeader>
        <h2>Filtri</h2>
        <CloseButton onClick={onClose}>×</CloseButton>
      </FilterHeader>

      <FilterSection>
        <h3>
          <i className="fas fa-search" style={{ marginRight: '8px', color: COLORS.primary || '#f39c12' }}></i>
          Cerca eventi
        </h3>
        <SearchForm onSubmit={handleSearchSubmit}>
          <SearchInput
            type="text"
            placeholder="Cerca per titolo, descrizione..."
            value={searchText}
            onChange={handleSearchChange}
          />
          {searchText ? (
            <SearchClearButton type="button" onClick={handleSearchClear} title="Cancella ricerca">×</SearchClearButton>
          ) : (
            <SearchIconButton type="submit" title="Avvia ricerca">
              <i className="fas fa-search" />
            </SearchIconButton>
          )}
        </SearchForm>
        {searchError && <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{searchError}</div>}
      </FilterSection>

      <FilterSection>
        <CategorySectionHeader onClick={() => !isMobile && setIsCategoriesCollapsed(!isCategoriesCollapsed)}>
          <h3>
            <i className="fas fa-tags" style={{ marginRight: '8px', color: COLORS.primary || '#f39c12' }}></i>
            Categorie
          </h3>
          {!isMobile && <CollapseIcon className="fas fa-chevron-down" isCollapsed={isCategoriesCollapsed} />}
        </CategorySectionHeader>
        <CategoryContent isCollapsed={!isMobile && isCategoriesCollapsed}>
          {isMobile ? renderMobileCategorySelector() : renderDesktopCategorySelector()}
        </CategoryContent>
      </FilterSection>
      
      <FilterSection>
        <h3>
          <i className="fas fa-calendar-alt" style={{ marginRight: '8px', color: COLORS.primary || '#f39c12' }}></i>
          Date
        </h3>
        {dateError && <div style={{ color: '#e74c3c', fontSize: '12px', marginBottom: '8px' }}>{dateError}</div>}
        <DateContainer>
          <DateLabel>Da:</DateLabel>
          <DateInput type="date" value={startDate} max={endDate || undefined} onChange={handleStartDateChange} />
        </DateContainer>
        <DateContainer>
          <DateLabel>A:</DateLabel>
          <DateInput type="date" value={endDate} min={startDate || undefined} onChange={handleEndDateChange} />
        </DateContainer>
        <DateQuickButtons>
          <QuickDateButton onClick={handleTodayClick}>Oggi</QuickDateButton>
          <QuickDateButton onClick={handleTomorrowClick}>Domani</QuickDateButton>
          <QuickDateButton onClick={handleWeekendClick}>Weekend</QuickDateButton>
        </DateQuickButtons>
      </FilterSection>
      
      {userLocation && (
        <FilterSection>
          <h3>
            <i className="fas fa-map-marker-alt" style={{ marginRight: '8px', color: COLORS.primary || '#f39c12' }}></i>
            Raggio di ricerca
          </h3>
          <RadiusContainer>
            <RadiusValue>{searchRadius} km</RadiusValue>
            <RadiusSlider type="range" min="1" max="200" value={searchRadius} onChange={(e) => setSearchRadius(parseInt(e.target.value))} />
            <RadiusLabels>
              <span>1 km</span>
              <span>100 km</span>
              <span>200 km</span>
            </RadiusLabels>
          </RadiusContainer>
        </FilterSection>
      )}
      
      <ButtonsContainer>
        <ApplyButton onClick={handleApplyFilters}>
          Applica filtri
        </ApplyButton>
        <ResetButton onClick={handleResetFilters}>
          Reimposta filtri
        </ResetButton>
      </ButtonsContainer>
    </FilterPanelContainer>
  );
};

export default FilterPanel;